import { useState } from 'react';

import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { usePopper } from 'react-popper';
import { Link } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import { Settings } from '@assets/icons';
import { Button, LockedButton, PermissionAccess } from '@components/index';
import { GENERAL_CONSTANTS } from '@constants/generalConstants';
import { UserPermissions } from '@enums/user-permissions';
import { useLogout } from '@hooks/use-logout';
import { AppRoutes } from '@routes/constants';
import { isSubscriptionAdminAtom } from '@store/atoms/userPermissions';
import { workspaceAtom } from '@store/atoms/workspace';

interface SettingsOption {
  id: string;
  label: string;
  path?: string;
  permission?: UserPermissions;
  showForSubscriptionAdmin?: boolean;
  isLogout?: boolean;
}

const SettingsOption = () => {
  const isSubscriptionAdminUser = useRecoilValue(isSubscriptionAdminAtom);
  const currentWorkspace = useRecoilValue(workspaceAtom);

  const { mutate: logout } = useLogout();

  const [referenceElement, setReferenceElement] =
    useState<HTMLButtonElement | null>();
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>();
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'right-start',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        },
      },
    ],
  });

  const settingsOptions: SettingsOption[] = [
    {
      id: 'workspaces',
      label: GENERAL_CONSTANTS.Workspaces,
      path: `/${currentWorkspace?.uuid}${AppRoutes.WORKSPACE}`,
      permission: UserPermissions.VIEW_WORKSPACES,
      showForSubscriptionAdmin: true,
    },
    {
      id: 'manage-team',
      label: GENERAL_CONSTANTS.ManageTeam,
      path: `/${currentWorkspace?.uuid}${AppRoutes.MANAGE_TEAM}`,
      permission: UserPermissions.VIEW_TEAMS,
    },
    {
      id: 'integrations',
      label: GENERAL_CONSTANTS.Integrations,
      path: `/${currentWorkspace?.uuid}${AppRoutes.INTEGRATIONS}`,
      permission: UserPermissions.VIEW_INTEGRATIONS,
    },
    {
      id: 'logout',
      label: 'Log out',
      isLogout: true,
    },
  ];

  const renderOption = (option: SettingsOption, close: () => void) => {
    if (option.showForSubscriptionAdmin && !isSubscriptionAdminUser) {
      return null;
    }

    if (option.isLogout) {
      return (
        <div key={option.id} className="px-3 py-2">
          <Button
            label={option.label}
            variant="error"
            onClick={() => {
              logout();
              close();
            }}
          />
        </div>
      );
    }

    if (option.permission && option.path) {
      return (
        <PermissionAccess
          key={option.id}
          permission={option.permission}
          AllowedContent={
            <Link to={option.path} onClick={close}>
              <div className="text-dark-1 text-sm flex h-10 px-3 cursor-pointer items-center hover:bg-lightest">
                {option.label}
              </div>
            </Link>
          }
          DeniedContent={
            <LockedButton
              buttonText={option.label}
              buttonClassname="w-full pl-3"
            />
          }
        />
      );
    }

    return null;
  };

  return (
    <Popover>
      {({ close }) => (
        <>
          <PopoverButton
            ref={
              setReferenceElement as (
                instance: HTMLButtonElement | null,
              ) => void
            }
            as="div"
            className="cursor-pointer z-30"
          >
            <div className="flex flex-row gap-2 items-center">
              <Settings className="size-4 text-gray-14" />
              <span className="text-[15px] font-semibold">
                {GENERAL_CONSTANTS.Settings}
              </span>
            </div>
          </PopoverButton>
          <PopoverPanel
            className="bg-white w-64 shadow-2xl rounded py-2 z-30"
            ref={element => setPopperElement(element as HTMLDivElement | null)}
            style={styles.popper}
            {...attributes.popper}
          >
            <div>
              {settingsOptions.map(option => renderOption(option, close))}
            </div>
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
};

export default SettingsOption;
